import type { BaseEntity, EntityHooks, SimpleTableConfig } from "../../UniversalCRMTable/types";
import { useActiveCustomFilter } from "./useActiveCustomFilter";

interface UseDataFetchingProps<T extends BaseEntity> {
  hooks: EntityHooks<T>;
  queryParams: {
    first: number;
    after: string | null;
    filter: Record<string, any>;
    searchTerm?: string;
  };
  config: SimpleTableConfig<T>;
  activeViewId: string | null;
}

/**
 * Handles data fetching via entity hooks
 * Calls: usePaginated for queries, useCreate/useUpdate/useDelete for mutations
 */
export function useDataFetching<T extends BaseEntity>({
  hooks,
  queryParams,
  config,
  activeViewId,
}: UseDataFetchingProps<T>) {
  // Query using EntityHooks - called unconditionally
  const entitiesQueryDefault = hooks.usePaginated(queryParams);

  // CRITICAL: Always call useActiveCustomFilter unconditionally
  // This hook internally calls all customFilterHooks unconditionally
  const entitiesQueryCustom = useActiveCustomFilter(config, activeViewId, queryParams);
  const entitiesQueryRaw = entitiesQueryCustom || entitiesQueryDefault;

  // Mutations using EntityHooks - called unconditionally
  const createMutation = hooks.useCreate();
  const updateMutation = hooks.useUpdate();
  const deleteMutation = hooks.useDelete();

  return {
    entitiesQueryRaw,
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
